import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type {
  CatalogModel,
  CatalogModelDetail,
  StudioAppProps,
  StudioChannel,
  StudioCollection,
  StudioTemplate,
  StudioView,
  WizardStepId,
} from './types';
import { WIZARD_STEPS } from './types';

type DraftVariant = {
  size: string;
  color: string;
  catalogRef: string;
  costAmount: number;
  retailAmount: number;
  currency: string;
};

function stepIndex(id: WizardStepId) {
  return WIZARD_STEPS.findIndex((s) => s.id === id);
}

export function StudioApp(props: StudioAppProps) {
  const { ports } = props;
  const shopUrl = (props.shopUrl || '').trim();
  const designerPageUrl = (props.designerPageUrl || '').trim();
  const [view, setView] = useState<StudioView>(props.initialView ?? 'list');
  const [templates, setTemplates] = useState<StudioTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [channels, setChannels] = useState<StudioChannel[]>([]);
  const [showConnect, setShowConnect] = useState(false);
  const [orderTemplate, setOrderTemplate] = useState<StudioTemplate | null>(null);

  // Wizard state
  const [step, setStep] = useState<WizardStepId>('product');
  const [template, setTemplate] = useState<StudioTemplate | null>(null);
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelDetail, setModelDetail] = useState<CatalogModelDetail | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [collections, setCollections] = useState<StudioCollection[]>([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [newCollection, setNewCollection] = useState('');
  /** List filter: empty = all collections */
  const [listCollectionFilter, setListCollectionFilter] = useState('');
  const [listNewCollection, setListNewCollection] = useState('');
  const [variants, setVariants] = useState<DraftVariant[]>([]);
  const [busy, setBusy] = useState(false);
  const [designReady, setDesignReady] = useState(false);
  /** Keep <ox-designer> mounted after first visit so Atrás restores the canvas. */
  const [designerAlive, setDesignerAlive] = useState(false);
  /** Bulk price tool: margin% | fixed profit | fixed retail */
  const [bulkMode, setBulkMode] = useState<'margin' | 'profit' | 'retail'>('margin');
  const [bulkValue, setBulkValue] = useState(50);
  /** Highest step index the user may jump to via the stepper. */
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [reviewViews, setReviewViews] = useState<
    Array<{ viewId: string; label: string; url: string }>
  >([]);
  const designerApiRef = useRef<{
    saveDesign: () => Promise<{ designId: string; versionId: string; objectCount?: number } | null>;
    assertReady?: () => void;
  } | null>(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [res, cols] = await Promise.all([
        ports.studio.listTemplates({
          limit: 50,
          ...(listCollectionFilter ? { collectionId: listCollectionFilter } : {}),
        }),
        ports.studio.listCollections(),
      ]);
      setTemplates(res.items ?? []);
      setCollections(cols.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  }, [ports.studio, listCollectionFilter]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  async function createListCollection() {
    const name = listNewCollection.trim();
    if (!name) return;
    setBusy(true);
    try {
      const c = await ports.studio.createCollection(name);
      setCollections((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)));
      setListNewCollection('');
      setListCollectionFilter(c.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear la colección');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!props.initialTemplateId) return;
    void (async () => {
      try {
        const t = await ports.studio.getTemplate(props.initialTemplateId!);
        openWizard(t);
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialTemplateId]);

  useEffect(() => {
    if (step === 'design' && template) setDesignerAlive(true);
  }, [step, template]);

  // Review: load design views that actually have artwork.
  useEffect(() => {
    if (step !== 'review' || !template?.designRef) {
      setReviewViews([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const base = (props.builderApiBase || '').replace(/\/$/, '');
      if (!base) return;
      try {
        const token = await ports.identity.getToken();
        const res = await fetch(`${base}/designs/${encodeURIComponent(template.designRef!)}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          document?: { views?: Array<{ viewId?: string; objects?: unknown[] }> };
        };
        const used = (data.document?.views ?? [])
          .filter((v) => v.viewId && Array.isArray(v.objects) && v.objects.length > 0)
          .map((v) => {
            const viewId = String(v.viewId);
            return {
              viewId,
              label: viewLabelEs(viewId),
              url: `${base}/designs/${encodeURIComponent(template.designRef!)}/preview?view=${encodeURIComponent(viewId)}&w=320&t=${Date.now()}`,
            };
          });
        if (!cancelled) {
          setReviewViews(
            used.length
              ? used
              : [
                  {
                    viewId: 'front',
                    label: viewLabelEs('front'),
                    url:
                      template.previewUrl ||
                      `${base}/designs/${encodeURIComponent(template.designRef!)}/preview?view=front&w=320`,
                  },
                ],
          );
        }
      } catch {
        if (!cancelled && template.designRef) {
          const base = (props.builderApiBase || '').replace(/\/$/, '');
          setReviewViews([
            {
              viewId: 'front',
              label: viewLabelEs('front'),
              url:
                template.previewUrl ||
                `${base}/designs/${encodeURIComponent(template.designRef)}/preview?view=front&w=320`,
            },
          ]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, template, ports.identity, props.builderApiBase]);

  useEffect(() => {
    const onSaved = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as {
        designId?: string;
        versionId?: string;
        templateId?: string;
        previewUrl?: string;
      };
      if (!template || !detail?.designId) return;
      setDesignReady(true);
      void ports.studio
        .updateTemplate(template.id, {
          designRef: detail.designId,
          designVersionRef: detail.versionId ?? null,
          wizardStep: 'details',
        })
        .then((t) => setTemplate(t));
    };
    window.addEventListener('ox:design-saved', onSaved);
    return () => window.removeEventListener('ox:design-saved', onSaved);
  }, [ports.studio, template]);

  function openWizard(t?: StudioTemplate) {
    if (t) {
      setTemplate(t);
      setTitle(t.title);
      setDescription(t.description);
      setSelectedModelId(t.catalogProductRef);
      setSelectedCollectionIds(t.collections.map((c) => c.id));
      setVariants(
        t.variants.map((v) => ({
          size: String(v.size ?? ''),
          color: String(v.color ?? ''),
          catalogRef: v.catalogRef,
          costAmount: v.costAmount,
          retailAmount: v.retailAmount,
          currency: v.currency,
        })),
      );
      setDesignReady(!!t.designRef);
      const s = (t.wizardStep as WizardStepId) || 'product';
      const next = WIZARD_STEPS.some((x) => x.id === s) ? s : 'product';
      setStep(next);
      setMaxStepReached(Math.max(stepIndex(next), t.designRef ? stepIndex('details') : 0));
      // Boot designer if resuming at/after design, or if a design already exists.
      setDesignerAlive(next === 'design' || !!t.designRef || stepIndex(next) > stepIndex('design'));
      setView('wizard');
      void hydrateWizard(t);
    } else {
      setTemplate(null);
      setTitle('');
      setDescription('');
      setSelectedModelId('');
      setModelDetail(null);
      setSelectedCollectionIds([]);
      setVariants([]);
      setDesignReady(false);
      setDesignerAlive(false);
      setMaxStepReached(0);
      setStep('product');
      setView('wizard');
    }
  }

  async function editTemplate(id: string) {
    setBusy(true);
    setError('');
    try {
      const t = await ports.studio.getTemplate(id);
      // Force a fresh designer mount so bootDesignId picks up designRef.
      setDesignerAlive(false);
      openWizard(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir la plantilla');
    } finally {
      setBusy(false);
    }
  }

  async function deleteTemplate(t: StudioTemplate) {
    const ok = window.confirm(
      `¿Eliminar la plantilla “${t.title || 'Sin título'}”? También se borrará el diseño vinculado y las imágenes subidas que no uses en otro diseño.`,
    );
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await ports.studio.deleteTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      if (orderTemplate?.id === t.id) setOrderTemplate(null);
      if (template?.id === t.id) {
        setTemplate(null);
        setView('list');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar la plantilla');
    } finally {
      setBusy(false);
    }
  }

  /** Resume a draft: load model, collections, and rebuild variants if missing. */
  async function hydrateWizard(t: StudioTemplate) {
    setBusy(true);
    setError('');
    try {
      const detail = await ports.catalog.getModel(t.catalogProductRef);
      if (detail && !(detail as { error?: string }).error) {
        setModelDetail(detail);
      }

      const stepId = (t.wizardStep as WizardStepId) || 'product';
      if (stepId === 'details' || stepId === 'prices' || stepId === 'review') {
        try {
          const cols = await ports.studio.listCollections();
          setCollections(cols.items ?? []);
        } catch {
          /* ignore */
        }
      }

      if (
        (stepId === 'prices' || stepId === 'review') &&
        (!t.variants || t.variants.length === 0)
      ) {
        const model = detail && !(detail as { error?: string }).error ? detail : null;
        if (model) {
          const next = await buildVariantsFromModel(model, ports, t.designRef || undefined);
          setVariants(next);
          if (next.length) {
            try {
              const updated = await ports.studio.putVariants(
                t.id,
                next.map((v, i) => ({ ...v, sortOrder: i, active: true })),
              );
              setTemplate(updated);
            } catch {
              /* keep local variants even if persist fails */
            }
          }
        } else {
          setError('No se pudo cargar el producto de la plantilla.');
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reabrir la plantilla');
    } finally {
      setBusy(false);
    }
  }

  async function loadModels() {
    setBusy(true);
    try {
      const list = await ports.catalog.listModels();
      setModels(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (view === 'wizard' && step === 'product') void loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, step]);

  async function selectProduct(modelId: string) {
    setSelectedModelId(modelId);
    setBusy(true);
    try {
      const detail = await ports.catalog.getModel(modelId);
      setModelDetail(detail);
      const modelName = detail?.name || models.find((m) => m.id === modelId)?.name || '';
      if (!title) setTitle(modelName);

      let t = template;
      if (!t) {
        t = await ports.studio.createTemplate({
          catalogProductRef: modelId,
          title: modelName,
        });
        setTemplate(t);
      } else {
        t = await ports.studio.updateTemplate(t.id, {
          catalogProductRef: modelId,
          title: title || modelName,
          wizardStep: 'design',
        });
        setTemplate(t);
      }
      setStep('design');
      setMaxStepReached((m) => Math.max(m, stepIndex('design')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear plantilla');
    } finally {
      setBusy(false);
    }
  }

  async function goDetails() {
    if (!template) return;
    setBusy(true);
    setError('');
    try {
      let saved: { designId: string; versionId: string; objectCount?: number } | null = null;
      for (let i = 0; i < 20; i++) {
        const api = designerApiRef.current;
        if (api?.saveDesign) {
          api.assertReady?.();
          saved = await api.saveDesign();
          break;
        }
        await new Promise((r) => setTimeout(r, 50));
      }
      if (!saved?.designId) {
        throw new Error('No se pudo guardar el diseño. Espera un momento y pulsa Continuar de nuevo.');
      }
      if ((saved.objectCount ?? 0) < 1) {
        throw new Error('Añade texto o una imagen al producto antes de continuar.');
      }
      setDesignReady(true);
      setDesignerAlive(true);
      const cols = await ports.studio.listCollections();
      setCollections(cols.items ?? []);
      const t = await ports.studio.updateTemplate(template.id, {
        designRef: saved.designId,
        designVersionRef: saved.versionId,
        wizardStep: 'details',
      });
      setTemplate(t);
      setStep('details');
      setMaxStepReached((m) => Math.max(m, stepIndex('details')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar el diseño');
    } finally {
      setBusy(false);
    }
  }

  async function saveDetailsAndPrices() {
    if (!template) return;
    setBusy(true);
    setError('');
    try {
      await ports.studio.updateTemplate(template.id, {
        title,
        description,
        collectionIds: selectedCollectionIds,
        wizardStep: 'prices',
      });

      let next = variants;
      if (!next.length) {
        let detail = modelDetail;
        if (!detail || (detail as { error?: string }).error) {
          detail = await ports.catalog.getModel(template.catalogProductRef);
          setModelDetail(detail);
        }
        if (!detail || (detail as { error?: string }).error) {
          throw new Error('No se pudo cargar el modelo para generar variantes');
        }
        next = await buildVariantsFromModel(detail, ports, template.designRef || undefined);
      } else {
        next = await refreshCosts(next, ports, template.designRef || undefined);
      }

      if (!next.length) {
        throw new Error('Este producto no tiene variantes (tallas/colores) configuradas');
      }

      setVariants(next);
      // Persist so Edit can resume without rebuilding from scratch
      const updated = await ports.studio.putVariants(
        template.id,
        next.map((v, i) => ({ ...v, sortOrder: i, active: true })),
      );
      setTemplate({ ...updated, wizardStep: 'prices' });
      setStep('prices');
      setMaxStepReached((m) => Math.max(m, stepIndex('prices')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar detalles');
    } finally {
      setBusy(false);
    }
  }

  async function savePricesAndReview() {
    if (!template) return;
    setBusy(true);
    try {
      await ports.studio.putVariants(
        template.id,
        variants.map((v, i) => ({ ...v, sortOrder: i, active: true })),
      );
      const t = await ports.studio.updateTemplate(template.id, { wizardStep: 'review' });
      setTemplate(t);
      setStep('review');
      setMaxStepReached((m) => Math.max(m, stepIndex('review')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar precios');
    } finally {
      setBusy(false);
    }
  }

  async function finishTemplate() {
    if (!template) return;
    setBusy(true);
    try {
      await ports.studio.updateTemplate(template.id, {
        title,
        description,
        status: 'ready',
        wizardStep: 'review',
        collectionIds: selectedCollectionIds,
      });
      setView('list');
      await refreshList();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setBusy(false);
    }
  }

  function applyBulkPricing() {
    const value = Number(bulkValue);
    if (!Number.isFinite(value) || value < 0) return;
    setVariants((prev) =>
      prev.map((row) => {
        const cost = Number(row.costAmount) || 0;
        let retail = row.retailAmount;
        if (bulkMode === 'margin') {
          // Margen sobre precio de venta (como Gelato): retail = cost / (1 - m/100)
          const m = Math.min(99.99, value);
          retail = m >= 100 ? cost : Math.round((cost / (1 - m / 100)) * 100) / 100;
        } else if (bulkMode === 'profit') {
          retail = Math.round((cost + value) * 100) / 100;
        } else {
          retail = Math.round(value * 100) / 100;
        }
        return { ...row, retailAmount: Math.max(0, retail) };
      }),
    );
  }

  async function placeOrder(t: StudioTemplate) {
    if (!t.designRef || !t.variants.length) {
      setError('La plantilla no tiene diseño o variantes.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // Bring-your-own garment print service: open the personalizer so the
      // buyer can pick print size / comments before adding to cart.
      if (await openDesignerForBringOwnOrder(t)) {
        return;
      }

      const lines = t.variants
        .filter((v) => v.active && v.catalogRef)
        .slice(0, 1)
        .map((v) => ({ variantId: v.catalogRef, quantity: 1 }));
      if (!lines.length) {
        setError('No hay variantes con referencia de catálogo.');
        return;
      }
      // Resolve print views (front/back/…) before cart so extras match the design.
      let viewMetrics: Array<Record<string, unknown>> | undefined;
      try {
        const quote = await ports.pricing.quote({
          lines,
          designId: t.designRef,
          forceRegularPrice: true,
        });
        if (Array.isArray(quote.viewMetrics) && quote.viewMetrics.length) {
          viewMetrics = quote.viewMetrics;
        }
        const extras = Number(quote.extrasUnit?.amount ?? 0);
        if (!viewMetrics?.length && extras <= 0) {
          // Still try cart with designId — server may resolve metrics.
        }
      } catch {
        /* cart will fall back to server-side design fetch */
      }
      const res = await ports.cart.addToCart({
        designId: t.designRef,
        versionId: t.designVersionRef || undefined,
        lines,
        viewMetrics,
        // Template orders always use catalog regular + print extras (never member cost).
        forceRegularPrice: true,
      });
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
      } else {
        setOrderTemplate(null);
        alert('Añadido al carrito.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo pedir');
    } finally {
      setBusy(false);
    }
  }

  /** @returns true when navigation to the designer was started. */
  async function openDesignerForBringOwnOrder(t: StudioTemplate): Promise<boolean> {
    if (!t.designRef || !t.catalogProductRef) return false;
    let detail: Awaited<ReturnType<typeof ports.catalog.getModel>> = null;
    try {
      detail = await ports.catalog.getModel(t.catalogProductRef);
    } catch {
      return false;
    }
    if (!detail?.printService || !detail.askGarmentColor) return false;

    const base = designerPageUrl;
    if (!base) {
      throw new Error(
        'No hay página de diseñador configurada. En Ox Designer elige la página design-tool.',
      );
    }

    const url = new URL(base, window.location.origin);
    url.searchParams.set('ox_model', t.catalogProductRef);
    url.searchParams.set('ox_design', t.designRef);
    if (t.designVersionRef) {
      url.searchParams.set('ox_version', t.designVersionRef);
    }

    const first = t.variants.find((v) => v.active && v.catalogRef) ?? t.variants[0];
    if (first?.catalogRef && Array.isArray(detail.variants)) {
      const match = detail.variants.find((v) => v.id === first.catalogRef);
      if (match?.colorKey) {
        url.searchParams.set('ox_color', match.colorKey);
      }
    }

    window.location.href = url.toString();
    return true;
  }

  async function startOrder(t: StudioTemplate) {
    setBusy(true);
    setError('');
    try {
      if (await openDesignerForBringOwnOrder(t)) {
        return;
      }
      setOrderTemplate(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir el pedido');
    } finally {
      setBusy(false);
    }
  }

  async function openConnectStore() {
    setShowConnect(true);
    try {
      const res = await ports.studio.listChannels();
      setChannels(res.items ?? []);
    } catch {
      setChannels([]);
    }
  }

  const currentStepIdx = stepIndex(step);

  function isStepUnlocked(id: WizardStepId): boolean {
    const i = stepIndex(id);
    if (i <= maxStepReached) return true;
    if (id === 'product') return true;
    if (id === 'design') return !!template?.catalogProductRef;
    if (id === 'details' || id === 'prices') return !!template?.designRef;
    if (id === 'review') return !!template?.designRef && variants.length > 0;
    return false;
  }

  function goToStep(id: WizardStepId) {
    if (busy || id === step || !isStepUnlocked(id)) return;
    if (id === 'design') setDesignerAlive(true);
    if (
      (id === 'details' || id === 'prices' || id === 'review') &&
      collections.length === 0
    ) {
      void ports.studio.listCollections().then((cols) => setCollections(cols.items ?? [])).catch(() => undefined);
    }
    setStep(id);
    setMaxStepReached((m) => Math.max(m, stepIndex(id)));
    setError('');
  }

  if (view === 'wizard') {
    return (
      <div className="oxs-root oxs-fullscreen">
        <div className="oxs-shell">
          <div className="oxs-head">
            <div className="oxs-head__titles">
              {shopUrl ? (
                <a className="oxs-back-shop" href={shopUrl}>
                  ← Volver a la tienda
                </a>
              ) : null}
              <h1>Crear plantilla</h1>
            </div>
            <button type="button" className="oxs-btn" onClick={() => { setView('list'); void refreshList(); }}>
              Volver a plantillas
            </button>
          </div>
          {error && <p className="oxs-error">{error}</p>}
          <div className="oxs-wizard">
            <nav className="oxs-stepper" aria-label="Pasos">
              {WIZARD_STEPS.map((s, i) => {
                const unlocked = isStepUnlocked(s.id);
                const active = i === currentStepIdx;
                const done = i < currentStepIdx;
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`oxs-step${active ? ' is-active' : ''}${done ? ' is-done' : ''}${unlocked && !active ? ' is-clickable' : ''}`}
                    disabled={!unlocked || busy}
                    aria-current={active ? 'step' : undefined}
                    onClick={() => goToStep(s.id)}
                  >
                    {s.label}
                  </button>
                );
              })}
            </nav>
            <div className="oxs-wizard__body">
              {step === 'product' && (
                <>
                  <h2>Escoge el producto</h2>
                  <p className="oxs-hint">Selecciona el modelo base de tu catálogo Ox.</p>
                  {busy && <p className="oxs-hint">Cargando…</p>}
                  <div className="oxs-models">
                    {models.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className={`oxs-model${selectedModelId === m.id ? ' is-selected' : ''}`}
                        onClick={() => void selectProduct(m.id)}
                        disabled={busy}
                      >
                        {m.thumbnail ? <img src={m.thumbnail} alt="" /> : <div />}
                        <strong>{m.name}</strong>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === 'design' && template && (
                <p className="oxs-hint oxs-hint--step">
                  Personaliza el producto. Al continuar se guarda o actualiza el diseño
                  automáticamente.
                  {designReady || template.designRef ? ' Diseño vinculado ✓' : ''}
                </p>
              )}

              {/* Keep designer mounted (hidden) so Atrás restores the canvas; still saved on Continuar. */}
              {template && (designerAlive || step === 'design') && (
                <div
                  className="oxs-designer-keep"
                  hidden={step !== 'design'}
                  style={
                    step === 'design'
                      ? undefined
                      : {
                          position: 'absolute',
                          width: 0,
                          height: 0,
                          overflow: 'hidden',
                          pointerEvents: 'none',
                        }
                  }
                >
                  <DesignerEmbed
                    modelId={template.catalogProductRef}
                    designId={template.designRef || undefined}
                    builderApiBase={props.builderApiBase || ''}
                    designerScriptUrl={props.designerScriptUrl}
                    designerAccent={props.designerAccent}
                    designerOnAccent={props.designerOnAccent}
                    designerHighlight={props.designerHighlight}
                    ports={ports}
                    apiRef={designerApiRef}
                  />
                </div>
              )}

              {step === 'details' && (
                <>
                  <h2>Detalles</h2>
                  <div className="oxs-field">
                    <label htmlFor="oxs-title">Título</label>
                    <input id="oxs-title" value={title} onChange={(e) => setTitle(e.target.value)} />
                  </div>
                  <div className="oxs-field">
                    <label htmlFor="oxs-desc">Descripción</label>
                    <textarea
                      id="oxs-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="oxs-field">
                    <label>Colecciones / tags</label>
                    <div className="oxs-tags">
                      {collections.map((c) => {
                        const on = selectedCollectionIds.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            className={`oxs-tag${on ? ' is-on' : ''}`}
                            onClick={() =>
                              setSelectedCollectionIds((prev) =>
                                on ? prev.filter((x) => x !== c.id) : [...prev, c.id],
                              )
                            }
                          >
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="oxs-collection-new">
                      <input
                        type="text"
                        placeholder="Nueva colección"
                        value={newCollection}
                        onChange={(e) => setNewCollection(e.target.value)}
                      />
                      <button
                        type="button"
                        className="oxs-btn"
                        disabled={!newCollection.trim() || busy}
                        onClick={() =>
                          void ports.studio.createCollection(newCollection.trim()).then((c) => {
                            setCollections((prev) => [...prev, c]);
                            setSelectedCollectionIds((prev) => [...prev, c.id]);
                            setNewCollection('');
                          })
                        }
                      >
                        Añadir
                      </button>
                    </div>
                  </div>
                </>
              )}

              {step === 'prices' && (
                <>
                  <h2>Precios y ganancia</h2>
                  <p className="oxs-hint">
                    El coste mostrado aquí es tu precio de miembro + impresión (vistas extra).
                    El margen fija el precio de venta. Al pedir o comprar la plantilla se cobra
                    el precio normal de catálogo + impresión (sin descuento de miembro).
                  </p>
                  {busy && !variants.length && <p className="oxs-hint">Cargando variantes…</p>}
                  {!busy && !variants.length && (
                    <p className="oxs-error">
                      No hay variantes. Vuelve a Detalles o pulsa «Actualizar costes».
                    </p>
                  )}

                  {variants.length > 0 && (
                    <div className="oxs-bulk-price">
                      <label className="oxs-bulk-price__label" htmlFor="oxs-bulk-mode">
                        Aplicar a todas las variantes
                      </label>
                      <div className="oxs-bulk-price__row">
                        <select
                          id="oxs-bulk-mode"
                          value={bulkMode}
                          onChange={(e) => {
                            const mode = e.target.value as 'margin' | 'profit' | 'retail';
                            setBulkMode(mode);
                            if (mode === 'margin') setBulkValue(50);
                            else if (mode === 'profit') setBulkValue(20);
                            else setBulkValue(50);
                          }}
                        >
                          <option value="margin">Fijar el margen de beneficios en</option>
                          <option value="profit">Fijar el beneficio en</option>
                          <option value="retail">Fijar el precio de venta en</option>
                        </select>
                        <input
                          type="number"
                          min={0}
                          step={bulkMode === 'margin' ? 1 : 0.01}
                          max={bulkMode === 'margin' ? 99 : undefined}
                          value={bulkValue}
                          onChange={(e) => setBulkValue(Number(e.target.value) || 0)}
                          aria-label="Valor"
                        />
                        <span className="oxs-bulk-price__unit">
                          {bulkMode === 'margin' ? '%' : variants[0]?.currency || 'PEN'}
                        </span>
                        <button
                          type="button"
                          className="oxs-btn oxs-btn--primary"
                          onClick={applyBulkPricing}
                        >
                          Actualizar
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: 'auto' }}>
                    <table className="oxs-table">
                      <thead>
                        <tr>
                          <th>Variante</th>
                          <th>Coste</th>
                          <th>Precio venta</th>
                          <th>Beneficio estimado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v, i) => {
                          const profit = Math.round((v.retailAmount - v.costAmount) * 100) / 100;
                          const marginPct =
                            v.retailAmount > 0
                              ? Math.round((profit / v.retailAmount) * 1000) / 10
                              : 0;
                          return (
                            <tr key={`${v.catalogRef}-${i}`}>
                              <td>
                                {v.color ? `${v.color} · ` : ''}
                                {String(v.size)}
                              </td>
                              <td>
                                {Number(v.costAmount).toFixed(2)} {v.currency}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={v.retailAmount}
                                  onChange={(e) => {
                                    const retailAmount = Number(e.target.value) || 0;
                                    setVariants((prev) =>
                                      prev.map((row, idx) =>
                                        idx === i ? { ...row, retailAmount } : row,
                                      ),
                                    );
                                  }}
                                />
                              </td>
                              <td className="oxs-profit">
                                <span className="oxs-profit__amount">
                                  {profit.toFixed(2)} {v.currency}
                                </span>
                                <span className="oxs-profit__pct">{marginPct}%</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="oxs-btn"
                    style={{ marginTop: 12 }}
                    disabled={busy || !template}
                    onClick={() => {
                      if (!template) return;
                      void (async () => {
                        setBusy(true);
                        try {
                          let detail = modelDetail;
                          if (!detail || (detail as { error?: string }).error) {
                            detail = await ports.catalog.getModel(template.catalogProductRef);
                            setModelDetail(detail);
                          }
                          let next = variants;
                          if (!next.length && detail && !(detail as { error?: string }).error) {
                            next = await buildVariantsFromModel(
                              detail,
                              ports,
                              template.designRef || undefined,
                            );
                          } else {
                            next = await refreshCosts(
                              next,
                              ports,
                              template.designRef || undefined,
                            );
                          }
                          setVariants(next);
                          if (next.length) {
                            await ports.studio.putVariants(
                              template.id,
                              next.map((v, i) => ({ ...v, sortOrder: i, active: true })),
                            );
                          }
                        } finally {
                          setBusy(false);
                        }
                      })();
                    }}
                  >
                    Actualizar costes
                  </button>
                </>
              )}

              {step === 'review' && template && (
                <>
                  <h2>Resumen del producto</h2>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                    {reviewViews.length > 0 ? (
                      <div className="oxs-review-views">
                        {reviewViews.map((v) => (
                          <figure key={v.viewId} className="oxs-review-view">
                            <img src={v.url} alt={v.label} width={160} height={160} />
                            <figcaption>{v.label}</figcaption>
                          </figure>
                        ))}
                      </div>
                    ) : template.previewUrl || template.designRef ? (
                      <img
                        src={
                          template.previewUrl ||
                          `${(props.builderApiBase || '').replace(/\/$/, '')}/designs/${template.designRef}/preview?view=front&w=280`
                        }
                        alt=""
                        width={160}
                        height={160}
                        style={{ objectFit: 'contain', background: '#f1f5f9', borderRadius: 8 }}
                      />
                    ) : null}
                    <div>
                      <p>
                        <strong>{title || template.title || 'Sin título'}</strong>
                      </p>
                      <p className="oxs-hint">{description || 'Sin descripción'}</p>
                      <p className="oxs-hint">
                        {variants.length} variantes
                        {selectedCollectionIds.length
                          ? ` · ${selectedCollectionIds.length} colección(es)`
                          : ''}
                        {reviewViews.length
                          ? ` · ${reviewViews.length} vista${reviewViews.length === 1 ? '' : 's'} con diseño`
                          : ''}
                      </p>
                    </div>
                  </div>

                  <h3 style={{ margin: '0 0 10px', fontSize: '1rem' }}>Variantes y precios</h3>
                  {!variants.length ? (
                    <p className="oxs-error">No hay precios definidos. Vuelve al paso Precios.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="oxs-table">
                        <thead>
                          <tr>
                            <th>Variante</th>
                            <th>Coste</th>
                            <th>Precio venta</th>
                            <th>Beneficio estimado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variants.map((v, i) => {
                            const profit =
                              Math.round((v.retailAmount - v.costAmount) * 100) / 100;
                            const marginPct =
                              v.retailAmount > 0
                                ? Math.round((profit / v.retailAmount) * 1000) / 10
                                : 0;
                            return (
                              <tr key={`${v.catalogRef}-review-${i}`}>
                                <td>
                                  {v.color ? `${v.color} · ` : ''}
                                  {String(v.size)}
                                </td>
                                <td>
                                  {Number(v.costAmount).toFixed(2)} {v.currency}
                                </td>
                                <td>
                                  <strong>
                                    {Number(v.retailAmount).toFixed(2)} {v.currency}
                                  </strong>
                                </td>
                                <td className="oxs-profit">
                                  <span className="oxs-profit__amount">
                                    {profit.toFixed(2)} {v.currency}
                                  </span>
                                  <span className="oxs-profit__pct">{marginPct}%</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="oxs-wizard__foot">
              <button
                type="button"
                className="oxs-btn"
                disabled={currentStepIdx <= 0 || busy}
                onClick={() => {
                  const prev = WIZARD_STEPS[currentStepIdx - 1];
                  if (prev) setStep(prev.id);
                }}
              >
                Atrás
              </button>
              {step === 'design' && (
                <button
                  type="button"
                  className="oxs-btn oxs-btn--primary"
                  disabled={busy}
                  onClick={() => void goDetails()}
                >
                  {busy ? 'Guardando…' : 'Continuar a Detalles'}
                </button>
              )}
              {step === 'details' && (
                <button
                  type="button"
                  className="oxs-btn oxs-btn--primary"
                  disabled={!title.trim() || busy}
                  onClick={() => void saveDetailsAndPrices()}
                >
                  Continuar a Precios
                </button>
              )}
              {step === 'prices' && (
                <button
                  type="button"
                  className="oxs-btn oxs-btn--primary"
                  disabled={!variants.length || busy}
                  onClick={() => void savePricesAndReview()}
                >
                  Continuar a Revisar
                </button>
              )}
              {step === 'review' && (
                <button
                  type="button"
                  className="oxs-btn oxs-btn--primary"
                  disabled={busy}
                  onClick={() => void finishTemplate()}
                >
                  Guardar plantilla
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="oxs-root">
      <div className="oxs-shell">
        <div className="oxs-head">
          <div className="oxs-head__titles">
            {shopUrl ? (
              <a className="oxs-back-shop" href={shopUrl}>
                ← Volver a la tienda
              </a>
            ) : null}
            <h1>Plantillas</h1>
          </div>
          <button type="button" className="oxs-btn oxs-btn--primary" onClick={() => openWizard()}>
            Crear una plantilla
          </button>
        </div>
        <div className="oxs-filters">
          <button
            type="button"
            className={`oxs-chip${!listCollectionFilter ? ' oxs-chip--on' : ''}`}
            onClick={() => setListCollectionFilter('')}
          >
            Todas
          </button>
          {collections.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`oxs-chip${listCollectionFilter === c.id ? ' oxs-chip--on' : ''}`}
              onClick={() => setListCollectionFilter(c.id)}
            >
              {c.name}
            </button>
          ))}
          <div className="oxs-filters__new oxs-collection-new">
            <input
              type="text"
              placeholder="Nueva colección"
              value={listNewCollection}
              onChange={(e) => setListNewCollection(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void createListCollection();
              }}
            />
            <button
              type="button"
              className="oxs-btn"
              disabled={!listNewCollection.trim() || busy}
              onClick={() => void createListCollection()}
            >
              Añadir
            </button>
          </div>
        </div>
        {error && <p className="oxs-error">{error}</p>}
        {loading ? (
          <p className="oxs-hint">Cargando…</p>
        ) : templates.length === 0 ? (
          <div className="oxs-empty">
            <p>
              {listCollectionFilter
                ? 'No hay plantillas en esta colección.'
                : 'Aún no tienes plantillas.'}
            </p>
            <button type="button" className="oxs-btn oxs-btn--primary" onClick={() => openWizard()}>
              Crear la primera
            </button>
          </div>
        ) : (
          <div className="oxs-grid">
            {templates.map((t) => (
              <article key={t.id} className="oxs-card">
                <div className="oxs-card__media">
                  {t.previewUrl ? <img src={t.previewUrl} alt="" /> : <span className="oxs-hint">Sin preview</span>}
                  <div className="oxs-card__actions">
                    <button
                      type="button"
                      className="oxs-btn oxs-btn--primary"
                      disabled={busy}
                      onClick={() => void startOrder(t)}
                    >
                      Haz un pedido
                    </button>
                  </div>
                </div>
                <div className="oxs-card__body">
                  <h3 className="oxs-card__title">{t.title || 'Sin título'}</h3>
                  {t.collections.length > 0 && (
                    <div className="oxs-card__tags">
                      {t.collections.map((c) => (
                        <span key={c.id} className="oxs-card__tag">
                          {c.name || c.slug || c.id}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="oxs-card__meta">
                    {t.status === 'draft' ? 'Borrador' : t.status}
                    {' · '}
                    {t.wizardStep}
                    {' · '}
                    {t.variants.map((v) => v.size).filter(Boolean).join(', ') || 'sin variantes'}
                  </p>
                  <p className="oxs-card__meta">
                    Creada {new Date(t.createdAt).toLocaleDateString()}
                  </p>
                  <button
                    type="button"
                    className="oxs-btn"
                    style={{ marginTop: 8, width: '100%' }}
                    onClick={() => void editTemplate(t.id)}
                    disabled={busy}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="oxs-btn oxs-btn--danger"
                    style={{ marginTop: 8, width: '100%' }}
                    onClick={() => void deleteTemplate(t)}
                    disabled={busy}
                  >
                    Eliminar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {showConnect && (
        <div className="oxs-modal" role="dialog" aria-modal="true">
          <div className="oxs-modal__backdrop" onClick={() => setShowConnect(false)} />
          <div className="oxs-modal__panel">
            <h2>Selección de tienda</h2>
            {channels.length === 0 ? (
              <p>Lo sentimos, no tienes tiendas conectadas.</p>
            ) : (
              <ul>
                {channels.map((c) => (
                  <li key={c.id}>
                    {c.name} ({c.kind}) — {c.status}
                  </li>
                ))}
              </ul>
            )}
            <div className="oxs-modal__actions">
              <button type="button" className="oxs-btn" disabled>
                Conectar tienda Shopify
              </button>
              <button type="button" className="oxs-btn" disabled>
                Conectar tienda WooCommerce
              </button>
              <button type="button" className="oxs-btn oxs-btn--primary" onClick={() => setShowConnect(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {orderTemplate && (
        <div className="oxs-modal" role="dialog" aria-modal="true">
          <div className="oxs-modal__backdrop" onClick={() => setOrderTemplate(null)} />
          <div className="oxs-modal__panel">
            <h2>Haz un pedido</h2>
            <p>
              Se añadirá «{orderTemplate.title || 'plantilla'}» al carrito al precio normal de
              catálogo + impresión (sin descuento de miembro).
            </p>
            <div className="oxs-modal__actions">
              <button
                type="button"
                className="oxs-btn oxs-btn--primary"
                disabled={busy}
                onClick={() => void placeOrder(orderTemplate)}
              >
                Añadir al carrito
              </button>
              <button type="button" className="oxs-btn" onClick={() => setOrderTemplate(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DesignerEmbed({
  modelId,
  designId,
  builderApiBase,
  designerScriptUrl,
  designerAccent,
  designerOnAccent,
  designerHighlight,
  ports,
  apiRef,
}: {
  modelId: string;
  designId?: string;
  builderApiBase: string;
  designerScriptUrl?: string;
  designerAccent?: string;
  designerOnAccent?: string;
  designerHighlight?: string;
  ports: StudioAppProps['ports'];
  apiRef: MutableRefObject<{
    saveDesign: () => Promise<{ designId: string; versionId: string; objectCount?: number } | null>;
    assertReady?: () => void;
  } | null>;
}) {
  const hostId = useMemo(() => `oxs-designer-${Math.random().toString(36).slice(2)}`, []);
  // Freeze the design id from first mount. Updating it after Continuar/save would
  // remount <ox-designer> via attributeChangedCallback and wipe the live canvas.
  const [bootDesignId] = useState(() => designId);

  useEffect(() => {
    let cancelled = false;
    async function mount() {
      if (designerScriptUrl && !customElements.get('ox-designer')) {
        await new Promise<void>((resolve, reject) => {
          const existing = document.querySelector(`script[src="${designerScriptUrl}"]`);
          if (existing) {
            existing.addEventListener('load', () => resolve());
            // Already loaded
            if (customElements.get('ox-designer')) resolve();
            else setTimeout(() => resolve(), 100);
            return;
          }
          const s = document.createElement('script');
          s.type = 'module';
          s.src = designerScriptUrl;
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('designer_script_failed'));
          document.head.appendChild(s);
        }).catch(() => undefined);
      }
      if (cancelled) return;
      const el = document.getElementById(hostId) as (HTMLElement & {
        setPorts?: (p: unknown) => void;
        saveDesign?: () => Promise<{ designId: string; versionId: string; objectCount?: number } | null>;
        assertReady?: () => void;
      }) | null;
      if (!el) return;

      const designerPorts = {
        catalog: ports.catalog,
        pricing: ports.pricing,
        cart: {
          getCart: async () => ({ cartCount: 0, redirectUrl: '' }),
          addToCart: ports.cart.addToCart,
        },
        identity: ports.identity,
      };
      const ready = () => {
        if (typeof el.setPorts === 'function') el.setPorts(designerPorts);
        else setTimeout(ready, 50);
      };
      ready();

      apiRef.current = {
        saveDesign: async () => {
          if (typeof el.saveDesign !== 'function') return null;
          return el.saveDesign();
        },
        assertReady: () => {
          el.assertReady?.();
        },
      };
    }
    void mount();
    return () => {
      cancelled = true;
      apiRef.current = null;
    };
  }, [hostId, designerScriptUrl, ports, modelId, apiRef]);

  return (
    <div className="oxs-designer-host">
      {/* @ts-expect-error custom element */}
      <ox-designer
        id={hostId}
        model={modelId}
        {...(bootDesignId ? { design: bootDesignId } : {})}
        api-base={builderApiBase}
        adapter="woo"
        commerce="0"
        {...(designerAccent ? { 'accent-color': designerAccent } : {})}
        {...(designerOnAccent ? { 'on-accent-color': designerOnAccent } : {})}
        {...(designerHighlight ? { 'highlight-color': designerHighlight } : {})}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
}

function viewLabelEs(viewId: string): string {
  const map: Record<string, string> = {
    front: 'Delante',
    back: 'Detrás',
    left: 'Izquierda',
    right: 'Derecha',
    sleeve: 'Manga',
    other: 'Otra',
  };
  return map[viewId] || viewId;
}

function sizeLabel(size: unknown): string {
  if (typeof size === 'string' || typeof size === 'number') return String(size);
  if (size && typeof size === 'object') {
    const o = size as { label?: unknown; id?: unknown; name?: unknown };
    if (typeof o.label === 'string' && o.label) return o.label;
    if (typeof o.id === 'string' && o.id) return o.id;
    if (typeof o.name === 'string' && o.name) return o.name;
  }
  return 'Única';
}

function sizeId(size: unknown): string {
  if (typeof size === 'string' || typeof size === 'number') return String(size);
  if (size && typeof size === 'object') {
    const o = size as { id?: unknown; label?: unknown };
    if (typeof o.id === 'string' && o.id) return o.id;
    if (typeof o.label === 'string' && o.label) return o.label;
  }
  return 'u';
}

async function buildVariantsFromModel(
  detail: CatalogModelDetail,
  ports: StudioAppProps['ports'],
  designId?: string,
): Promise<DraftVariant[]> {
  // Prefer Woo model variants (already have woo:productId:variationId + price)
  if (Array.isArray(detail.variants) && detail.variants.length > 0) {
    const colorName = (key?: string) => {
      if (!key) return '';
      const c = detail.colors?.find((x) => x.key === key);
      return c?.name || key;
    };
    const drafts: DraftVariant[] = detail.variants.map((v) => {
      const cost = typeof v.price?.amount === 'number' ? v.price.amount : 0;
      const currency = v.price?.currency || 'PEN';
      return {
        size: sizeLabel(
          detail.sizes?.find((s) => sizeId(s) === v.sizeId) ?? v.sizeId ?? 'u',
        ),
        color: colorName(v.colorKey),
        catalogRef: v.id,
        costAmount: cost,
        retailAmount: cost > 0 ? Math.round(cost * 2 * 100) / 100 : 0,
        currency,
      };
    });
    // Refresh via quote when refs are woo: (member pricing)
    return refreshCosts(drafts.slice(0, 40), ports, designId);
  }

  const colors = detail.colors?.length
    ? detail.colors
    : [{ key: 'default', name: '', sizes: detail.sizes }];
  const drafts: DraftVariant[] = [];

  for (const color of colors) {
    const rawSizes =
      color.sizes?.length ? color.sizes : detail.sizes?.length ? detail.sizes : ['S', 'M', 'L', 'XL'];
    const productId = color.productId ?? detail.wooProductId ?? 0;
    for (const raw of rawSizes) {
      const label = sizeLabel(raw);
      const sid = sizeId(raw);
      const variation =
        color.variations?.find(
          (v) =>
            (v.size && (v.size === sid || v.size === label)) ||
            Object.values(v.attributes || {}).some(
              (a) => String(a) === sid || String(a) === label,
            ),
        ) ?? null;
      const variationId = variation?.id ?? 0;
      const catalogRef =
        productId > 0
          ? `woo:${productId}:${variationId}`
          : `model:${detail.id}:${color.key}:${sid}`;
      drafts.push({
        size: label,
        color: color.name || color.key || '',
        catalogRef,
        costAmount: 0,
        retailAmount: 0,
        currency: 'PEN',
      });
    }
  }

  return refreshCosts(drafts.slice(0, 40), ports, designId);
}

async function refreshCosts(
  variants: DraftVariant[],
  ports: StudioAppProps['ports'],
  designId?: string,
): Promise<DraftVariant[]> {
  const quoteable = variants.filter((v) => v.catalogRef.startsWith('woo:'));
  if (!quoteable.length) {
    return variants.map((v) => ({
      ...v,
      costAmount: v.costAmount || 0,
      retailAmount: v.retailAmount || Math.round((v.costAmount || 0) * 2 * 100) / 100,
    }));
  }

  try {
    const designRef = designId && designId !== 'draft' ? designId : undefined;
    // Quote all variants for base catalog prices, and include designId so Woo
    // resolves used views (front/back/…) into extrasUnit (print surcharge).
    const quote = await ports.pricing.quote({
      lines: quoteable.map((v) => ({ variantId: v.catalogRef, quantity: 1 })),
      designId: designRef,
    });
    const byId = new Map(quote.lines.map((l) => [l.variantId, l]));
    const extrasUnit = Number(quote.extrasUnit?.amount ?? 0) || 0;
    return variants.map((v) => {
      const line = byId.get(v.catalogRef);
      const base = line?.unitPrice.amount ?? v.costAmount;
      const cost = Math.round((Number(base) + extrasUnit) * 100) / 100;
      const currency = line?.unitPrice.currency ?? v.currency;
      // Keep an existing retail if the member already set it; otherwise seed 2× cost.
      const retail =
        v.retailAmount > 0 ? v.retailAmount : Math.round(cost * 2 * 100) / 100;
      return { ...v, costAmount: cost, retailAmount: retail, currency };
    });
  } catch {
    return variants;
  }
}
